import { memo, useLayoutEffect } from "react";

import type { TaskBox } from "@/components/ui/box.schema";
import { useSessionWorkspace } from "@/hooks/use-session-workspace";
import { ApiError } from "@/lib/api/client";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";

import { ClassPlanCard, type SessionPlanParts } from "../ClassPlanCard";
import { usePrepareBox } from "../SessionPrepare";
import type { SessionRowReport, SessionTaskKind } from "./row-task";
import { useCloseBox } from "./use-close-box";

type Stale = { readAt: string; retry: () => void } | undefined;

function PrepareReport({ workspace, parts, stale, report }: { workspace: SessionWorkspace; parts: SessionPlanParts; stale: Stale; report: (next: SessionRowReport) => void }) {
  const { box } = usePrepareBox({ workspace, parts, rank: null });
  useLayoutEffect(() => report({ status: "ready", workspace, box: box as TaskBox<unknown>, stale }));
  return null;
}

function CloseReport({ workspace, parts, stale, report }: { workspace: SessionWorkspace; parts: SessionPlanParts; stale: Stale; report: (next: SessionRowReport) => void }) {
  const box = useCloseBox({ workspace, parts, rank: null });
  useLayoutEffect(() => report({ status: "ready", workspace, box: box as TaskBox<unknown> | null, stale }));
  return null;
}

/**
 * Hosts one session's Prepare or Close for a List that opens it in a row
 * (K3, CASE-019): it reads that session on its own, holds the Task's state
 * and drafts, and reports the Task up. It draws nothing itself; the List
 * draws the Task's parts and foot in the row. Memoised, so the report it
 * sends never draws it again.
 */
export const SessionRowTaskHost = memo(function SessionRowTaskHost({ sessionId, kind, onReport }: {
  sessionId: string;
  kind: SessionTaskKind;
  onReport: (sessionId: string, kind: SessionTaskKind, report: SessionRowReport) => void;
}) {
  const query = useSessionWorkspace(sessionId);
  // A refused read wins over any cached workspace: its plan, roll and commit are no longer drawn or sent (R1, CASE-018).
  const refused = query.error instanceof ApiError && [401, 403, 404].includes(query.error.status);
  const workspace = !refused && query.data?.session.id === sessionId ? query.data : undefined;
  const report = (next: SessionRowReport) => onReport(sessionId, kind, next);
  const unread = refused ? "refused" : workspace ? null : query.isPending ? "loading" : "failed";
  const stale: Stale = workspace && query.isError ? { readAt: new Date(query.dataUpdatedAt).toISOString(), retry: () => void query.refetch() } : undefined;
  useLayoutEffect(() => {
    if (unread === "refused") onReport(sessionId, kind, { status: "refused" });
    if (unread === "loading") onReport(sessionId, kind, { status: "loading" });
    if (unread === "failed") onReport(sessionId, kind, { status: "failed", retry: () => void query.refetch() });
  }, [unread, sessionId, kind, onReport, query]);
  if (!workspace) return null;
  const canEdit = workspace.course.term_open && (workspace.viewer.allowed_actions.includes("create_session_plan") || workspace.viewer.allowed_actions.includes("edit_session_plan"));
  return (
    <ClassPlanCard workspace={workspace} canEdit={canEdit} renderTasks={(parts) => (kind === "prepare"
      ? <PrepareReport workspace={workspace} parts={parts} stale={stale} report={report} />
      : <CloseReport workspace={workspace} parts={parts} stale={stale} report={report} />)} />
  );
});
